import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { issueFileToken } from "./dmsApi";

/**
 * DmsPreviewProvider — singleton state for the right-docked DMS panel.
 *
 * Wrap your <App> with <DmsPreviewProvider><App /></DmsPreviewProvider>
 * and place <DmsPreviewPanel /> as a sibling of <App>'s top element.
 * Then anywhere in the tree:
 *
 *   const dms = useDmsPreview();
 *   dms.show({ ref_type: 'gate_entry_doc_id', ref_id: 'SCN-...', emp_id });
 *   dms.showPage(2);   // navigate to page 2 of the currently-shown doc
 *   dms.hide();
 *
 * State shape:
 *   open:        whether panel is open
 *   loading:     whether fetching the file token
 *   error:       last error message, if any
 *   request:     { ref_type, ref_id, emp_id, page, zoom, label } currently displayed
 *   url:         iframe src (with PDF Open Parameters if page/zoom set)
 *   filename:    display name in panel header
 *   width:       current panel width in px (resizable)
 *   mode:        'overlay' | 'push'
 */

const DmsPreviewContext = createContext(null);

const STORAGE_KEY_WIDTH = "myerp.dms.panelWidth";
const STORAGE_KEY_MODE  = "myerp.dms.panelMode";

const DEFAULT_WIDTH = 560;
const MIN_WIDTH     = 320;
const MAX_WIDTH     = 1400;

const readPersisted = (key, fallback) => {
  try {
    const v = window.localStorage.getItem(key);
    return v == null ? fallback : v;
  } catch { return fallback; }
};
const writePersisted = (key, val) => {
  try { window.localStorage.setItem(key, String(val)); } catch {}
};

export function DmsPreviewProvider({ children }) {
  const [open,     setOpen]     = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [request,  setRequest]  = useState(null);   // { ref_type, ref_id, emp_id, page, zoom, label }
  const [url,      setUrl]      = useState(null);   // iframe src
  const [filename, setFilename] = useState(null);

  const [width, setWidth] = useState(() => {
    const v = parseInt(readPersisted(STORAGE_KEY_WIDTH, DEFAULT_WIDTH), 10);
    return Number.isFinite(v) && v >= MIN_WIDTH && v <= MAX_WIDTH ? v : DEFAULT_WIDTH;
  });
  const [mode, setMode] = useState(() => {
    const v = readPersisted(STORAGE_KEY_MODE, "overlay");
    return v === "push" ? "push" : "overlay";
  });

  // Persist width + mode on change
  useEffect(() => { writePersisted(STORAGE_KEY_WIDTH, width); }, [width]);
  useEffect(() => { writePersisted(STORAGE_KEY_MODE,  mode);  }, [mode]);

  // Esc closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Build iframe URL with optional PDF Open Parameters (page, zoom)
  const buildUrl = useCallback((baseUrl, page, zoom) => {
    if (!baseUrl) return baseUrl;
    const frags = [];
    if (page) frags.push(`page=${page}`);
    if (zoom) frags.push(`zoom=${zoom}`);
    if (frags.length === 0) return baseUrl;
    const sep = baseUrl.includes("#") ? "&" : "#";
    return `${baseUrl}${sep}${frags.join("&")}`;
  }, []);

  /**
   * Fetch a fresh token and open the panel.
   * @param {object} req   { ref_type, ref_id, emp_id, page?, zoom?, label? }
   */
  const show = useCallback(async (req) => {
    if (!req || !req.ref_type || !req.ref_id) {
      setError("show() requires { ref_type, ref_id }");
      return;
    }
    setLoading(true);
    setError(null);
    setOpen(true);
    setRequest(req);
    try {
      const r = await issueFileToken({
        ref_type: req.ref_type,
        ref_id:   req.ref_id,
        emp_id:   req.emp_id,
      });
      if (!r.ok || !r.url) {
        setError(r.error || r.detail || "Failed to issue file token");
        setUrl(null);
        return;
      }
      setUrl(buildUrl(r.url, req.page, req.zoom));
      setFilename(r.filename || `${req.ref_id}.pdf`);
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.detail || e.message);
      setUrl(null);
    } finally {
      setLoading(false);
    }
  }, [buildUrl]);

  /**
   * Navigate to a different page WITHOUT refetching the token.
   * (URL fragment changes navigate within the same iframe load.)
   *
   * NOTE: Token is single-use. After it's been consumed by the iframe's
   * initial load, the page-fragment navigation works because the browser
   * has already cached the bytes. If the iframe is reloaded for any
   * reason, we'd need a fresh token. For most operator workflows this
   * is fine — they navigate pages within an open viewing session.
   */
  const showPage = useCallback((page) => {
    if (!url) return;
    if (!request) return;
    // Strip existing fragment, append new one
    const base = url.split("#")[0];
    setUrl(buildUrl(base, page, request.zoom));
    setRequest({ ...request, page });
  }, [url, request, buildUrl]);

  /** Update the current request (page, zoom) and rebuild URL. */
  const update = useCallback((patch) => {
    if (!url || !request) return;
    const next = { ...request, ...patch };
    const base = url.split("#")[0];
    setUrl(buildUrl(base, next.page, next.zoom));
    setRequest(next);
  }, [url, request, buildUrl]);

  const hide = useCallback(() => setOpen(false), []);

  const toggleMode = useCallback(() => {
    setMode(m => m === "overlay" ? "push" : "overlay");
  }, []);

  const value = useMemo(() => ({
    // state
    open, loading, error, request, url, filename, width, mode,
    // actions
    show, showPage, update, hide, toggleMode,
    setWidth: (w) => setWidth(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, w))),
    constraints: { MIN_WIDTH, MAX_WIDTH, DEFAULT_WIDTH },
  }), [open, loading, error, request, url, filename, width, mode,
       show, showPage, update, hide, toggleMode]);

  return (
    <DmsPreviewContext.Provider value={value}>
      {children}
    </DmsPreviewContext.Provider>
  );
}

/**
 * Hook for any component to control the DMS preview panel.
 * Usage:
 *   const dms = useDmsPreview();
 *   dms.show({ ref_type, ref_id, emp_id });
 */
export function useDmsPreview() {
  const ctx = useContext(DmsPreviewContext);
  if (!ctx) {
    throw new Error("useDmsPreview must be used within <DmsPreviewProvider>");
  }
  return ctx;
}
